export interface PromptStageMeta {
  subFeature:
    | 'headings'
    | 'headings_briefs'
    | 'plan_review'
    | 'plan_fix'
    | 'full_article'
    | 'critique'
    | 'section_revision'
    | 'title_revision'
    | 'language_polish'
    | 'span_revision'
    | 'seo_meta'
    | 'heading_rules'
    | 'hard_rules';
  order: number;
  recipient: 'writer' | 'critics' | 'shared';
  /** 'block' rows are not called: they fill %SUB_FEATURE% in other prompts */
  kind?: 'stage' | 'block';
  placeholders: { name: string; description: string; requiredIn: 'user' | 'none' }[];
  jsonContract: string | null;
}

const BASE = [
  'LANGUAGE',
  'TONE_OF_VOICE',
  'CUSTOMER_GROUP_DESCRIPTION',
  'DOMAIN_NAME',
  'PRIMARY_KEYWORD',
  'SECONDARY_KEYWORDS',
  'SOURCE_NOTES',
  'QUOTES',
  'SOURCE_DATA',
  'HEADING_COUNT',
]
  .map((name) => ({ name, description: 'Base input, substituted for every stage', requiredIn: 'none' as const }))
  .concat([
    {
      name: 'SECTION_LENGTH',
      description: "The user's target size per section (about 50-100 / 100-200 / 200-350 words; a guideline)",
      requiredIn: 'none' as const,
    },
    {
      name: 'LEARNINGS',
      description: 'Stored editor notes for this domain (Learnings tab), newest first',
      requiredIn: 'none' as const,
    },
    {
      name: 'HEADING_RULES',
      description: 'Rule block: the heading rules (edited once under Rule blocks)',
      requiredIn: 'none' as const,
    },
    {
      name: 'HARD_RULES',
      description: 'Rule block: the hard rules (edited once under Rule blocks)',
      requiredIn: 'none' as const,
    },
  ]);

export const PROMPT_STAGES: PromptStageMeta[] = [
  {
    subFeature: 'headings',
    order: 1,
    recipient: 'writer',
    placeholders: BASE,
    jsonContract: '{"h1": string, "headings": string[]}',
  },
  {
    subFeature: 'headings_briefs',
    order: 2,
    recipient: 'writer',
    placeholders: [
      ...BASE,
      { name: 'H1', description: 'The approved H1', requiredIn: 'user' },
      { name: 'HEADINGS', description: 'Numbered subheading list', requiredIn: 'user' },
    ],
    jsonContract: '{"sections": [{"heading": string, "brief": string}]}',
  },
  {
    subFeature: 'plan_review',
    order: 3,
    recipient: 'critics',
    placeholders: [
      ...BASE,
      { name: 'EVIDENCE', description: 'What the inputs support (computed)', requiredIn: 'none' },
      { name: 'ARTIFACT', description: 'The plan: H1, headings and briefs', requiredIn: 'user' },
      { name: 'TARGET', description: "'headings' or 'headings and briefs'", requiredIn: 'none' },
    ],
    jsonContract:
      '{"h1": {"ok": boolean, "suggestion": string}, "headings": [{"index", "ok", "suggestion"}], "briefs": [...], "structure_notes": string}',
  },
  {
    subFeature: 'plan_fix',
    order: 4,
    recipient: 'writer',
    placeholders: [
      ...BASE,
      { name: 'EVIDENCE', description: 'What the inputs support (computed)', requiredIn: 'none' },
      { name: 'ARTIFACT', description: 'The current plan', requiredIn: 'user' },
      { name: 'REVIEW_SUGGESTIONS', description: "The reviewer's fix list", requiredIn: 'user' },
    ],
    jsonContract: '{"h1": string, "sections": [{"heading": string, "brief": string}]}',
  },
  {
    subFeature: 'full_article',
    order: 5,
    recipient: 'writer',
    placeholders: [
      ...BASE,
      { name: 'H1', description: 'The approved H1', requiredIn: 'none' },
      { name: 'STRUCTURE', description: 'Numbered subheading list', requiredIn: 'none' },
      { name: 'BRIEFS', description: 'All section briefs', requiredIn: 'none' },
      { name: 'ARTICLE_SO_FAR', description: 'Sections already written', requiredIn: 'none' },
      { name: 'SECTION_HEADING', description: 'The section being written', requiredIn: 'user' },
      { name: 'SECTION_BRIEF', description: 'The section brief', requiredIn: 'user' },
    ],
    jsonContract: null,
  },
  {
    subFeature: 'critique',
    order: 6,
    recipient: 'critics',
    placeholders: [
      ...BASE,
      { name: 'ARTIFACT', description: 'The full text under review', requiredIn: 'user' },
      { name: 'TARGET', description: "'skeleton' or 'article'", requiredIn: 'user' },
      { name: 'ROUND', description: 'Critique round number', requiredIn: 'none' },
    ],
    jsonContract:
      '{"verdict": "positive"|"negative", "h1": {"ok", "feedback"}, "dimensions": {"headings": {...}, "sections": [{"index", "findings": [{"severity", "text"}], "score": 1-5}], "logic": {...}, "flow": {...}, "human_feel": {...}, "added_value": {...}}}',
  },
  {
    subFeature: 'section_revision',
    order: 7,
    recipient: 'writer',
    placeholders: [
      ...BASE,
      { name: 'TARGET', description: "'skeleton' or 'article'", requiredIn: 'none' },
      { name: 'ROUND', description: 'Critique round number', requiredIn: 'none' },
      { name: 'SECTION_INDEX', description: 'Index of the section being revised', requiredIn: 'user' },
      { name: 'SECTION_HEADING', description: 'Heading of the section being revised', requiredIn: 'none' },
      { name: 'SECTION_CURRENT', description: 'Current text of the section', requiredIn: 'user' },
      { name: 'SECTION_FEEDBACK', description: "Every critic's feedback on this section (this round)", requiredIn: 'user' },
      { name: 'SECTION_HISTORY', description: "Earlier rounds' feedback on this section", requiredIn: 'none' },
      { name: 'ACCEPTED_SECTIONS', description: 'Sections already accepted (final)', requiredIn: 'none' },
      { name: 'ARTIFACT', description: 'The whole current text, for context', requiredIn: 'none' },
    ],
    jsonContract: 'skeleton target: {"heading": string, "brief": string} — article target: the section body in markdown',
  },
  {
    subFeature: 'title_revision',
    order: 8,
    recipient: 'writer',
    placeholders: [
      ...BASE,
      { name: 'H1_FEEDBACK', description: "Critics' feedback on the H1", requiredIn: 'user' },
      { name: 'ARTIFACT', description: 'The article', requiredIn: 'none' },
    ],
    jsonContract: '{"h1": string}',
  },
  {
    subFeature: 'language_polish',
    order: 9,
    recipient: 'writer',
    placeholders: [
      ...BASE,
      { name: 'TARGET', description: "'plan' or 'article section'", requiredIn: 'user' },
      { name: 'ARTIFACT', description: 'The text to polish (a section body, or the plan)', requiredIn: 'user' },
      { name: 'SECTION_HEADING', description: 'Heading of the section being polished', requiredIn: 'none' },
    ],
    jsonContract:
      'article section: the polished body in markdown — plan: {"h1": string, "sections": [{"heading": string, "brief": string}]}',
  },
  {
    subFeature: 'span_revision',
    order: 10,
    recipient: 'writer',
    placeholders: [
      ...BASE,
      { name: 'SECTION_HEADING', description: 'Heading of the section the passage is in', requiredIn: 'none' },
      { name: 'SECTION_CURRENT', description: 'The whole section as it stands (context only)', requiredIn: 'user' },
      { name: 'SELECTED_TEXT', description: 'The highlighted passage to rewrite', requiredIn: 'user' },
      { name: 'NOTE', description: "The editor's note: how the passage should change", requiredIn: 'user' },
    ],
    jsonContract: 'the replacement passage only, in markdown (no JSON, no fences)',
  },
  {
    subFeature: 'seo_meta',
    order: 11,
    recipient: 'writer',
    placeholders: [
      ...BASE,
      { name: 'BRAND', description: 'The company name the title ends with', requiredIn: 'none' },
      { name: 'TARGET', description: "'plan' or 'article'", requiredIn: 'user' },
      { name: 'H1', description: 'The approved H1', requiredIn: 'user' },
      { name: 'ARTIFACT', description: 'The article, or the plan of headings and briefs', requiredIn: 'user' },
    ],
    jsonContract: '{"title": string, "metaDescription": string}',
  },
  {
    subFeature: 'heading_rules',
    order: 12,
    recipient: 'shared',
    kind: 'block',
    placeholders: [],
    jsonContract: null,
  },
  {
    subFeature: 'hard_rules',
    order: 13,
    recipient: 'shared',
    kind: 'block',
    placeholders: [
      { name: 'EVIDENCE', description: 'What the inputs support (computed)', requiredIn: 'none' },
    ],
    jsonContract: null,
  },
];
